import React from 'react';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import { useTranslation } from 'src/i18n/shim/useTranslation';
import styles from './DetailsPanel.module.scss';

/** A single key-value row rendered by {@link DetailsPanel}. */
export interface DetailsRow {
  /** Left-aligned label. Also used as the React key, so it must be unique. */
  label: string;
  /** Right-aligned value. May be plain text or richer content (badge, link). */
  value: React.ReactNode;
  /** Optional test id applied to the value cell for integration tests. */
  testId?: string;
  /**
   * Marks a value that renders its own block element (e.g. an ExpandableCell's
   * horizontal SpaceBetween) so that element is laid out as a right-aligned,
   * wrapping flex row within this narrow column. Opt-in per row: a plain value
   * — or one that merely happens to render a `<div>` — is left untouched.
   */
  rich?: boolean;
}

interface DetailsPanelProps {
  rows: DetailsRow[];
}

/**
 * Shared "Details" panel presenting an ordered list of key-value rows.
 *
 * Both the string group detail page and the string detail page render metadata
 * through this single component so the two panels look identical; each page
 * maps its own domain object to {@link DetailsRow}s and passes them in.
 *
 * `fitHeight` makes the Container fill its stretched column/grid cell so its
 * bottom aligns with the taller panel beside it, rather than rendering at its
 * shorter natural content height and leaving a gap below.
 */
const DetailsPanel: React.FC<DetailsPanelProps> = ({ rows }) => {
  const { t } = useTranslation();

  return (
    <Container
      header={<Header variant="h3">{t('details-panel-header')}</Header>}
      fitHeight
    >
      <div className={styles.body}>
        {rows.map((row) => (
          <div key={row.label} className={styles.row}>
            <span className={styles.label}>{row.label}</span>
            <div
              className={
                row.rich ? `${styles.value} ${styles.valueRich}` : styles.value
              }
              data-testid={row.testId}
            >
              {row.value}
            </div>
          </div>
        ))}
      </div>
    </Container>
  );
};

export default DetailsPanel;
